import { useState } from 'react';
// Piezas compartidas de búsqueda y filtros (todas las secciones usan las mismas)
export const norm = (s) => (s ?? '').toString().normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().trim();
export const matchesQuery = (q, ...fields) => { const n = norm(q); return !n || fields.some((f) => norm(f).includes(n)); };

const inputCls = 'w-full px-3 py-2 rounded-xl border border-gray-200 text-[13px] bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-sky-200';
const selCls = 'min-w-0 flex-1 basis-[140px] px-3 py-2 rounded-xl border border-gray-200 text-[13px] font-medium bg-white text-gray-600 focus:outline-none focus:ring-2 focus:ring-sky-200 truncate';

export function SearchBox({ value, onChange, placeholder = 'Buscar…', className = '' }) {
  return (
    <div className={`relative basis-full ${className}`}>
      <svg className="w-3.5 h-3.5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" viewBox="0 0 24 24"><circle cx="11" cy="11" r="7" /><path d="M20 20l-3.5-3.5" /></svg>
      <input value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} aria-label={placeholder}
        className={`${inputCls} pl-9 pr-8`} />
      {value && <button type="button" onClick={() => onChange('')} aria-label="Borrar búsqueda" className="absolute right-2 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full text-gray-400 hover:bg-gray-100 flex items-center justify-center text-base leading-none">×</button>}
    </div>
  );
}

// Selector compacto (lista desplegable). Con tabs=true se muestra como pestañas de navegación.
export function Chips({ options, value, onChange, tabs = false, label }) {
  if (tabs) {
    return (
      <div className="flex gap-1.5 overflow-x-auto no-scrollbar -mx-1 px-1">
        {options.map((o) => (
          <button key={o.id} type="button" onClick={() => onChange(o.id)}
            className={`shrink-0 px-3 py-1.5 rounded-xl text-xs font-bold transition-colors ${value === o.id ? 'bg-sky-100 text-sky-700' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'}`}>
            {o.label}{o.count != null && <span className="ml-1.5 opacity-60">{o.count}</span>}
          </button>
        ))}
      </div>
    );
  }
  return (
    <select value={value ?? ''} onChange={(e) => onChange(e.target.value)} aria-label={label || 'Filtrar'} className={selCls}>
      {options.map((o) => <option key={o.id} value={o.id}>{o.label}{o.count != null ? ` (${o.count})` : ''}</option>)}
    </select>
  );
}

export function SelectFilter({ value, onChange, options, allLabel = 'Todos', label }) {
  return (
    <select value={value} onChange={(e) => onChange(e.target.value)} aria-label={label || allLabel} className={selCls}>
      <option value="all">{allLabel}</option>
      {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  );
}

const iso = (d) => [d.getFullYear(), String(d.getMonth() + 1).padStart(2, '0'), String(d.getDate()).padStart(2, '0')].join('-');
export const presetRange = (id) => {
  const now = new Date();
  const day = (n) => { const d = new Date(now); d.setDate(d.getDate() + n); return d; };
  if (id === 'today') return { from: iso(now), to: iso(now) };
  if (id === 'yesterday') return { from: iso(day(-1)), to: iso(day(-1)) };
  if (id === 'week') return { from: iso(day(-6)), to: iso(now) };
  if (id === 'month') return { from: iso(new Date(now.getFullYear(), now.getMonth(), 1)), to: iso(now) };
  if (id === 'lastmonth') return { from: iso(new Date(now.getFullYear(), now.getMonth() - 1, 1)), to: iso(new Date(now.getFullYear(), now.getMonth(), 0)) };
  return { from: '', to: '' };
};
const PRESETS = [['today', 'Hoy'], ['yesterday', 'Ayer'], ['week', '7 días'], ['month', 'Este mes'], ['lastmonth', 'Mes pasado'], ['all', 'Todo']];

export function DateRange({ from, to, onChange, presets = true }) {
  const [custom, setCustom] = useState(false);
  const active = PRESETS.find(([id]) => { const r = presetRange(id); return r.from === from && r.to === to; })?.[0];
  const mode = custom || (!active && (from || to)) ? 'custom' : (active || 'all');
  return (
    <>
      {presets && (
        <select value={mode} aria-label="Período" className={selCls}
          onChange={(e) => { const v = e.target.value; if (v === 'custom') setCustom(true); else { setCustom(false); onChange(presetRange(v)); } }}>
          {PRESETS.map(([id, l]) => <option key={id} value={id}>{l}</option>)}
          <option value="custom">Elegir fechas…</option>
        </select>
      )}
      {(mode === 'custom' || !presets) && (
        <div className="basis-full flex items-center gap-2">
          <input type="date" aria-label="Desde" value={from} max={to || undefined} onChange={(e) => onChange({ from: e.target.value, to })} className={`${inputCls} min-w-0 flex-1`} />
          <span className="text-gray-300 text-xs">→</span>
          <input type="date" aria-label="Hasta" value={to} min={from || undefined} onChange={(e) => onChange({ from, to: e.target.value })} className={`${inputCls} min-w-0 flex-1`} />
        </div>
      )}
    </>
  );
}

// Contenedor: tarjeta con los filtros, cantidad de resultados y botón para limpiar
export function FilterBar({ children, shown, total, noun = 'resultados', active, onClear }) {
  return (
    <div className="bg-white rounded-2xl border border-gray-100 p-3 space-y-2">
      <div className="flex flex-wrap gap-2 [&>div]:basis-full [&_.flex-wrap]:contents">{children}</div>
      <div className="flex items-center justify-between text-[11px] text-gray-400">
        <span>{shown != null && total != null ? (shown === total ? `${total} ${noun}` : `${shown} de ${total} ${noun}`) : ''}</span>
        {active && onClear && <button type="button" onClick={onClear} className="font-semibold text-sky-700 hover:underline">Limpiar filtros</button>}
      </div>
    </div>
  );
}

// Fila de totales (tarjetitas) para encabezar una lista. items: [{label, value, tone}]
const TONES = { default: 'bg-gray-50 text-gray-800', sky: 'bg-sky-50 text-sky-800', green: 'bg-emerald-50 text-emerald-700', red: 'bg-red-50 text-red-600', amber: 'bg-amber-50 text-amber-700' };
export function TotalsBar({ items }) {
  const list = items.filter(Boolean);
  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
      {list.map((it) => (
        <div key={it.label} className={`rounded-2xl px-3.5 py-3 ${TONES[it.tone || 'default']}`}>
          <p className="text-[10px] font-bold uppercase tracking-wide opacity-60">{it.label}</p>
          <p className="text-lg font-black leading-tight mt-0.5 break-words">{it.value}</p>
        </div>
      ))}
    </div>
  );
}
export const fmtHM = (mins) => { const m = Math.round(mins || 0); return `${Math.floor(m / 60)} h ${String(m % 60).padStart(2, '0')} m`; };
